import { cn } from '@labflow/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { Fragment, type ReactNode, useEffect, useRef } from 'react';

import type { SubNavEntry } from '../../lib/navigation';

/**
 * 영역 안 2차 이동 — D3 §4.6 `SubNav`의 최소 구현. `packages/ui`에 생기면 교체한다.
 *
 * 링크 목록이지 탭이 아니다(D145 ⑨). 현재 항목 하나만 `aria-current="page"`이고, 활성 표시는
 * 즉시 바뀐다(D145 ⑩). search param은 활성 판정에 쓰지 않는다(D145 ⑤).
 */
export interface SubNavProps {
  /** `<nav>`의 접근 이름 — 예: "My Lab 메뉴" */
  label: string;
  items: ReadonlyArray<SubNavEntry>;
  /** @default 'horizontal' */
  orientation?: 'horizontal' | 'vertical' | 'responsive';
  /** 목록 끝, 구분선 뒤에 붙는 동작 — 계정 목차의 로그아웃 */
  footer?: ReactNode;
  className?: string;
}

const itemBase =
  'hover:text-ink ease-flow duration-fast relative flex items-center text-sm font-medium whitespace-nowrap transition-colors';

export function SubNav({
  label,
  items,
  orientation = 'horizontal',
  footer,
  className,
}: SubNavProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const listRef = useRef<HTMLUListElement>(null);

  // 넘치는 가로 줄에서도 활성 항목이 늘 보이게 한다 (D145 ⑧)
  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
    active?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [pathname]);

  const vertical = orientation === 'vertical';
  const responsive = orientation === 'responsive';

  return (
    <nav aria-label={label} className={className}>
      <ul
        ref={listRef}
        className={cn(
          'flex',
          vertical ? 'gap-tight flex-col' : 'gap-card h-10 items-stretch overflow-x-auto',
          responsive &&
            'split:gap-tight split:h-auto split:flex-col split:items-stretch split:overflow-visible',
        )}
      >
        {items.map((item, index) => {
          const groupStart = index > 0 && item.group !== items[index - 1]!.group;
          return (
            <Fragment key={item.key}>
              {groupStart ? <Separator orientation={orientation} /> : null}
              <li className="flex">
                <Link
                  to={item.to}
                  activeOptions={{ exact: item.exact ?? false, includeSearch: false }}
                  activeProps={{ className: 'text-action' }}
                  inactiveProps={{ className: 'text-ink-muted' }}
                  className={cn(
                    itemBase,
                    vertical ? 'px-snug h-9 rounded-md' : 'h-full',
                    responsive && 'split:px-snug split:h-9 split:rounded-md',
                  )}
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      {isActive ? (
                        <span
                          aria-hidden="true"
                          className={cn(
                            'bg-selected pointer-events-none absolute',
                            vertical ? 'inset-y-1.5 left-0 w-0.5' : 'inset-x-0 bottom-0 h-0.5',
                            responsive &&
                              'split:inset-x-auto split:inset-y-1.5 split:left-0 split:h-auto split:w-0.5',
                          )}
                        />
                      ) : null}
                    </>
                  )}
                </Link>
              </li>
            </Fragment>
          );
        })}
        {footer ? (
          <>
            <Separator orientation={orientation} />
            <li className="flex">{footer}</li>
          </>
        ) : null}
      </ul>
    </nav>
  );
}

function Separator({ orientation }: { orientation: SubNavProps['orientation'] }) {
  return (
    <li
      role="presentation"
      aria-hidden="true"
      className={cn(
        'bg-line shrink-0',
        orientation === 'vertical' ? 'my-tight h-px w-full' : 'h-4 w-px self-center',
        orientation === 'responsive' && 'split:my-tight split:h-px split:w-full split:self-auto',
      )}
    />
  );
}
